import clsx from 'clsx'
import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import { useQueryStates } from 'nuqs'
import React, { useContext, useMemo } from 'react'

import { ampli, NavLinkClickProperties } from '@Analytics/ampli'
import { getColorMapping } from '@Components/Facet/Display/Items/color-mappings'
import { ShowcaseCarouselSlider, ShowcaseCarouselSliderItem } from '@Components/Slider/Carousel/Showcase'
import { TagItem, Tags } from '@Components/Tags'
import { Wordbook } from '@Components/Wordbook'
import { ReferenceContentFragment, WordbookFragmentFragment } from '@Generated/graphql'
import { ProductSearchContext, ProductSearchStaticContext } from '@Globals/Algolia/Context'
import { createQueryStatesKeys } from '@Globals/Algolia/Shared'
import {
  FacetDynamicConfigFacet,
  FacetDynamicConfigValue,
  PageProductListBlockGuidedFilter,
} from '@Globals/Algolia/Types'
import { gtagEvent } from '@Globals/Google/GTag'
import { pageTypeToDestinationPageType } from '@Globals/Page/Mappers'

import styles from './GuidedFilters.module.scss'

type PageProductListBlockGuidedFilterAttributeEnum = Extract<PageProductListBlockGuidedFilter, { __typename: 'GuidedFilterAttributeEnum' }>
type PageProductListBlockGuidedFilterAttributeEnumItem = NonNullable<PageProductListBlockGuidedFilterAttributeEnum['items']>[0]
type PageProductListBlockGuidedFilterAttributeEnumItemEnhanced = PageProductListBlockGuidedFilterAttributeEnumItem & {
  id: string
  onClick: () => void
  text: WordbookFragmentFragment | null
}
type PageProductListBlockGuidedFilterAttributeEnumEnhanced = Omit<PageProductListBlockGuidedFilterAttributeEnum, 'header' | 'items' | 'text'> & {
  header?: WordbookFragmentFragment | null
  id: string
  items: PageProductListBlockGuidedFilterAttributeEnumItemEnhanced[]
  text?: WordbookFragmentFragment | null
}

type PageProductListBlockGuidedFilterLinkResource = Extract<PageProductListBlockGuidedFilter, { __typename: 'GuidedFilterLinkResource' }>
type PageProductListBlockGuidedFilterLinkResourceItem = NonNullable<PageProductListBlockGuidedFilterLinkResource['items']>[0]
type PageProductListBlockGuidedFilterLinkResourceItemEnhanced = PageProductListBlockGuidedFilterLinkResourceItem & {
  analytics: NavLinkClickProperties
  asset: any
  href: string
  id: string
  onClick: () => void
  target?: string
  text: WordbookFragmentFragment | null
}
type PageProductListBlockGuidedFilterLinkResourceEnhanced = Omit<PageProductListBlockGuidedFilterLinkResource, 'header' | 'items' | 'text'> & {
  header?: WordbookFragmentFragment | null
  id: string
  items: PageProductListBlockGuidedFilterLinkResourceItemEnhanced[]
  text?: WordbookFragmentFragment | null
}

type PageProductListBlockGuidedFilterEnhanced =
  | PageProductListBlockGuidedFilterAttributeEnumEnhanced
  | PageProductListBlockGuidedFilterLinkResourceEnhanced

export function useGuidedFilters(): PageProductListBlockGuidedFilterEnhanced[] {
  const { facetConfig } = useContext(ProductSearchStaticContext)
  const { facetDynamicConfig, guidedFilters } = useContext(ProductSearchContext)
  const queryStatesKeysMap = useMemo(() => createQueryStatesKeys(facetConfig), [facetConfig])
  const [, setQueryStates] = useQueryStates(queryStatesKeysMap)

  return useMemo(() => sortBy(
    compact(
      map(
        guidedFilters,
        (guidedFilter, guidedFilterIndex) => {
          if (guidedFilter.__typename === 'GuidedFilterAttributeEnum') {
            const facetConfigFacet = facetDynamicConfig[guidedFilter.parameter || ''] as FacetDynamicConfigFacet | undefined
            if (!facetConfigFacet || facetConfigFacet.count !== 0) {
              return null
            }

            const items = sortBy(
              compact(map(guidedFilter.items, (item, itemIndex): PageProductListBlockGuidedFilterAttributeEnumItemEnhanced | null => {
                const facetConfigValue = facetDynamicConfig[`${guidedFilter.parameter}:${item.key}`] as FacetDynamicConfigValue | undefined
                if (!facetConfigValue || facetConfigValue.count === 0) {
                  return null
                }

                return {
                  ...item,
                  backgroundColor: guidedFilter.component === 'tags'
                    ? getColorMapping(facetConfigValue.key, item.backgroundColor || '', true)
                    : item.backgroundColor,
                  id: `${item.__typename}-${itemIndex}`,
                  onClick: () => {
                    ampli.guidedFiltersClick({
                      guided_filter: {
                        index: guidedFilterIndex,
                        item_index: itemIndex,
                        type: 'attribute_enum',
                        attribute: guidedFilter.attribute || '',
                        attribute_key: item.key || '',
                      },
                    })

                    if (!guidedFilter.parameter || !item.key) {
                      return
                    }

                    setQueryStates((prev) => {
                      let enabled = false
                      let nextValue = prev[guidedFilter.parameter!] as string[] | null

                      if (!nextValue) {
                        enabled = true
                        nextValue = [item.key!]
                      } else if (!nextValue.includes(item.key!)) {
                        enabled = true
                        nextValue = [...nextValue, item.key!].sort()
                      } else if (nextValue.length > 1) {
                        nextValue = nextValue.filter(v => v !== item.key!).sort()
                      } else {
                        nextValue = null
                      }

                      if (enabled) {
                        ampli.plApplyFilter({
                          filter_code: guidedFilter.parameter,
                          filter_option_id: item.key,
                        })
                        gtagEvent('filter_changed', {
                          filter_code: guidedFilter.parameter,
                          filter_option_id: item.key,
                        })
                      }

                      return {
                        ...prev,
                        [guidedFilter.parameter!]: nextValue,
                        page: null,
                      }
                    })
                  },
                  text: facetConfigValue.name || { defaults: item.key },
                  textColor: guidedFilter.component === 'tags'
                    ? getColorMapping(facetConfigValue.key || '', item.textColor || '', false)
                    : item.textColor,
                }
              })),
              item => -(item.priority || 0),
            )

            if (isEmpty(items)) {
              return null
            }

            let header: WordbookFragmentFragment | null = guidedFilter.header?.value ? { defaults: guidedFilter.header.value } : null
            if (!header) {
              header = facetConfigFacet.name || { defaults: guidedFilter.attribute || '' }
            }

            const text = guidedFilter.text?.value || ''

            const result: PageProductListBlockGuidedFilterAttributeEnumEnhanced = {
              ...guidedFilter,
              header,
              id: `${guidedFilter.__typename}-${guidedFilterIndex}`,
              items,
              text: { defaults: text },
            }

            return result
          }

          if (guidedFilter.__typename === 'GuidedFilterLinkResource') {
            const items = sortBy(
              compact(map(guidedFilter.items, (item, itemIndex): PageProductListBlockGuidedFilterLinkResourceItemEnhanced | null => {
                if (!item || !item.link) {
                  return null
                }

                const linkReference = item.link?.references?.[0]
                if (linkReference?.__typename !== 'Page') {
                  return null
                }

                let href = ''

                try {
                  const url = new URL(item.link?.url || '')
                  if (item.link?.queryParameters) {
                    const searchParams = new URLSearchParams(item.link.queryParameters)
                    searchParams.forEach((value, key) => {
                      url.searchParams.append(key, value)
                    })
                  }
                  href = url.toString()
                } catch {
                  // ignore errors
                }

                if (!href) {
                  return null
                }

                return {
                  ...item,
                  analytics: {
                    dest_page_type: pageTypeToDestinationPageType(linkReference.type || ''),
                    dest_page_type_id: linkReference.id || '',
                    source_id: 'guided_filter',
                  },
                  asset: item.link,
                  href,
                  id: `${item.__typename}-${itemIndex}`,
                  onClick: () => ampli.guidedFiltersClick({
                    guided_filter: {
                      index: guidedFilterIndex,
                      item_index: itemIndex,
                      type: 'link_resource',
                      link_resource_type: linkReference.type || '',
                      link_resource_id: linkReference.id || '',
                    },
                  }),
                  target: item.link.newTab ? '_blank' : '',
                  text: { defaults: linkReference.title || '' },
                }
              })),
              item => -(item.priority || 0),
            )

            if (isEmpty(items)) {
              return null
            }

            const result: PageProductListBlockGuidedFilterLinkResourceEnhanced = {
              ...guidedFilter,
              header: { defaults: guidedFilter.header?.value || '' },
              id: `${guidedFilter.__typename}-${guidedFilterIndex}`,
              items,
              text: { defaults: guidedFilter.text?.value || '' },
            }

            return result
          }

          return null
        },
      ),
    ),
    item => -(item.priority || 0),
  ), [facetDynamicConfig, guidedFilters, setQueryStates])
}

export const GuidedFilterShowcaseCarousel = ({
  guidedFilter,
  row,
  scrollToTop,
  style,
}: {
  guidedFilter: PageProductListBlockGuidedFilterEnhanced
  row: number
  scrollToTop?: () => void
  style?: React.CSSProperties
}) => {
  if (guidedFilter.component === 'showcase-carousel') {
    return (
      <ShowcaseCarouselSlider
        key={guidedFilter.id}
        background={guidedFilter.asset as ReferenceContentFragment}
        backgroundColor={guidedFilter.backgroundColor || undefined}
        className={clsx(
          styles.guidedFilter,
          guidedFilter.variant && guidedFilter.variant === 'spot' && styles.spotSlider,
          guidedFilter.variant && guidedFilter.variant === 'background' && styles.backgroundSlider,
          guidedFilter.variant && guidedFilter.variant === 'background-without-text' && styles.backgroundWithoutTextSlider,
        )}
        data-row={row}
        header={<Wordbook wordbook={guidedFilter.header} />}
        sizes="(max-width: 1024px) 100vw, (max-width: 1200px) 75vw, (max-width: 1600px) 80vw, 85vw"
        style={style}
        text={<Wordbook wordbook={guidedFilter.text} />}
        textColor={guidedFilter.textColor || undefined}
      >
        {guidedFilter.items.map(item => (
          <ShowcaseCarouselSliderItem
            key={item.id}
            analytics={item.analytics || undefined}
            background={item.asset as ReferenceContentFragment}
            backgroundColor={item.backgroundColor || undefined}
            href={'href' in item ? item.href : undefined}
            onClick={
              'onClick' in item
                ? (e) => {
                  if ('href' in item) {
                    item.onClick()
                    scrollToTop?.()
                    return
                  }

                  e.preventDefault()
                  item.onClick()
                  scrollToTop?.()
                }
                : undefined
            }
            target={item.target || undefined}
            textColor={item.textColor || undefined}
            variant={guidedFilter.variant as any || undefined}
          >
            <Wordbook wordbook={item.text} />
          </ShowcaseCarouselSliderItem>
        ))}
      </ShowcaseCarouselSlider>
    )
  }

  if (guidedFilter.component === 'tags') {
    return (
      <Tags
        key={guidedFilter.id}
        background={guidedFilter.asset as ReferenceContentFragment}
        backgroundColor={guidedFilter.backgroundColor || undefined}
        className={clsx(styles.guidedFilter, styles.tags)}
        data-row={row}
        header={<Wordbook wordbook={guidedFilter.header} />}
        sizes="(max-width: 1024px) 100vw, (max-width: 1200px) 75vw, (max-width: 1600px) 80vw, 85vw"
        style={style}
        text={<Wordbook wordbook={guidedFilter.text} />}
        textColor={guidedFilter.textColor || undefined}
      >
        {guidedFilter.items.map(item => (
          <TagItem
            key={item.id}
            analytics={item.analytics || undefined}
            background={item.asset as ReferenceContentFragment}
            backgroundColor={item.backgroundColor || undefined}
            href={'href' in item ? item.href : undefined}
            onClick={
              'onClick' in item
                ? (e) => {
                  if ('href' in item) {
                    item.onClick()
                    scrollToTop?.()
                    return
                  }

                  e.preventDefault()
                  item.onClick()
                  scrollToTop?.()
                }
                : undefined
            }
            target={item.target || undefined}
            textColor={item.textColor || undefined}
            variant={guidedFilter.variant as any || undefined}
          >
            <Wordbook wordbook={item.text} />
          </TagItem>
        ))}
      </Tags>
    )
  }

  return null
}
